import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, ExternalLink, Globe2 } from 'lucide-react';
import { api, type ProjectSummary } from '../api/client';
import { StatusBadge } from './StatusBadge';
import { cn } from '../lib/cn';

interface Props {
  project: ProjectSummary;
  onOpen: () => void;
}

export function SiteCard({ project, onOpen }: Props) {
  const { data } = useQuery({
    queryKey: ['projectState', project.id],
    queryFn: () => api.getProjectState(project.id),
    refetchInterval: (q) => {
      const s = q.state.data?.buildRun?.status;
      return s === 'running' || s === 'queued' ? 2500 : 12000;
    },
  });

  const status = data?.buildRun?.status;
  const liveUrl = data?.liveUrl ?? null;
  const phase = data?.buildRun?.lastCompletedPhase;
  const isActive = status === 'running' || status === 'queued';
  const displayName = project.displayName || project.brandId;
  const hostname = liveUrl
    ? liveUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'group relative text-left bg-white border border-zinc-200/80 rounded-xl shadow-card',
        'transition-all duration-200 ease-out hover:shadow-card-hover hover:border-zinc-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2',
        'p-4 sm:p-5 flex flex-col gap-3 min-h-[160px]',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-zinc-900">
            <div className="h-7 w-7 shrink-0 rounded-md bg-gradient-to-br from-zinc-100 to-zinc-200 text-zinc-600 flex items-center justify-center">
              <Globe2 className="h-3.5 w-3.5" />
            </div>
            <h3 className="font-semibold tracking-tightish text-[14.5px] truncate">
              {displayName}
            </h3>
          </div>
        </div>
        <StatusBadge status={status} hasLiveUrl={!!liveUrl} />
      </div>

      <div className="min-h-[20px]">
        {hostname ? (
          <div className="flex items-center gap-1.5 text-[12.5px] text-zinc-500 font-mono truncate">
            <span className="truncate">{hostname}</span>
            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        ) : isActive ? (
          <div className="text-[12.5px] text-zinc-500">
            {phase ? `Working on ${phase}…` : 'Generating site…'}
          </div>
        ) : (
          <div className="text-[12.5px] text-zinc-400">No live URL yet</div>
        )}
      </div>

      <div className="mt-auto flex items-end justify-between gap-3 text-[11.5px] text-zinc-400">
        <span className="truncate">
          {project.kitId} · updated {formatRelative(project.updatedAt)}
        </span>
        <ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 group-hover:text-zinc-700 transition" />
      </div>

      {isActive && (
        <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden rounded-b-xl">
          <div className="h-full bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-shimmer bg-[length:200%_100%]" />
        </div>
      )}
    </button>
  );
}

export function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
